import NoMovementCustomerModal from '@/components/Customers/NoMovementCustomerModal';
import { useDisclosure } from '@/hooks';
import { SHOW_WITHOUT_MOVIMENTS } from '@/utils/user/constants';
import { Checkbox, Image, Modal, Typography } from 'antd';
import { PropsWithChildren, createContext, useContext, useState } from 'react';
import { useMarketingPublicationActive } from '@/core/domain/communication/marketingPublication/marketingPublication.hooks';
import { useMarketingPublicationInteraction } from '@/core/domain/communication/marketingPublicationInteraction/marketingPublicationInteraction.hooks';
import moment from 'moment';
import { UseDisclosureActions } from '@/hooks/useDisclosure';

interface MainContextParams {
  modalWithoutMoviments: {
    opened: boolean;
    toggle: VoidFunction;
    show: boolean;
  };
  popoverNotification: {
    opened: boolean;
    action: UseDisclosureActions;
  };

  openMarketingPublication: VoidFunction;
}

const MainContext = createContext({} as MainContextParams);

export const useMainContext = () => useContext(MainContext);

const MainContextProvider: React.FC<PropsWithChildren<unknown>> = (props) => {
  const showWithOutMoviments = JSON.parse(
    localStorage.getItem(SHOW_WITHOUT_MOVIMENTS) || 'false',
  ) as boolean;

  const [popoverNotificationOpened, popoverNotificationAction] =
    useDisclosure(false);

  const [modalWithoutMovimentsOpened, modalWithoutMovimentsAction] =
    useDisclosure(false);

  const [marketingPublicationOpened, { toggle: marketingPublicationToggle }] =
    useDisclosure(false);

  const [hiddenModalIndication, setHiddenModalIndication] = useState(false);
  const [markAsClicked, setMarkAsClicked] = useState(false);

  function modalWithoutMovimentToggle(checked: boolean) {
    localStorage.setItem(SHOW_WITHOUT_MOVIMENTS, JSON.stringify(checked));
  }

  const marketingPublication = useMarketingPublicationActive();
  const marketingPublicationInteraction = useMarketingPublicationInteraction();

  async function openMarketingPublication() {
    const result = await marketingPublication.mutateAsync();
    if (!!result && moment().isSameOrBefore(moment(result.expirationDate)))
      marketingPublicationToggle();
  }

  async function closeMarketingPublication() {
    marketingPublicationToggle();

    if (marketingPublication.data)
      marketingPublicationInteraction.mutateAsync({
        id: marketingPublication.data.id,
        markAsClicked: markAsClicked,
        setAsDontShowAgain: hiddenModalIndication,
      });
  }

  return (
    <MainContext.Provider
      value={{
        modalWithoutMoviments: {
          opened: modalWithoutMovimentsOpened,
          toggle: modalWithoutMovimentsAction.toggle,
          show: showWithOutMoviments,
        },

        popoverNotification: {
          opened: popoverNotificationOpened,
          action: popoverNotificationAction,
        },

        openMarketingPublication,
      }}
    >
      <>
        {props.children}

        {modalWithoutMovimentsOpened && (
          <NoMovementCustomerModal
            open={modalWithoutMovimentsOpened}
            onCancel={modalWithoutMovimentsAction.close}
            onChange={modalWithoutMovimentToggle}
            show={showWithOutMoviments}
          />
        )}

        <Modal
          open={marketingPublicationOpened}
          onCancel={closeMarketingPublication}
          title={marketingPublication.data?.title}
          width={900}
          footer={false}
        >
          <Typography.Text>
            {marketingPublication.data?.description}
          </Typography.Text>

          <a
            href={marketingPublication.data?.redirectionUrl}
            target="_blank"
            onClick={() => {
              closeMarketingPublication();
              setMarkAsClicked(true);
            }}
          >
            <Image
              src={marketingPublication.data?.imageUrl}
              width="100%"
              preview={false}
            />
          </a>

          <div style={{ marginTop: 24 }}>
            <Checkbox
              checked={hiddenModalIndication}
              onChange={({ target }) =>
                setHiddenModalIndication(target.checked)
              }
            >
              Não exibir a propaganda novamente
            </Checkbox>
          </div>
        </Modal>
      </>
    </MainContext.Provider>
  );
};

export default MainContextProvider;
